import React, { useState, useRef, useEffect } from 'react';

interface LazyVideoProps {
  src: string;
  className?: string;
  placeholderClassName?: string;
  muted?: boolean;
  loop?: boolean;
  autoPlayOnHover?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * 懒加载视频组件
 * 使用 IntersectionObserver 实现视口内加载
 * 支持鼠标悬停自动播放
 */
export default function LazyVideo({
  src,
  className = '',
  placeholderClassName = '',
  muted = true,
  loop = true,
  autoPlayOnHover = true,
  onLoad,
  onError
}: LazyVideoProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoadedData = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const handleMouseEnter = () => {
    if (autoPlayOnHover && videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  };

  const handleMouseLeave = () => {
    if (autoPlayOnHover && videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${placeholderClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 占位骨架屏 */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-bg-elevated animate-pulse rounded-inherit" />
      )}

      {/* 错误状态 */}
      {hasError && (
        <div className="absolute inset-0 bg-bg-surface flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
      )}

      {/* 实际视频 */}
      {isInView && !hasError && (
        <video
          ref={videoRef}
          src={src}
          className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          muted={muted}
          loop={loop}
          playsInline
          preload="metadata"
          onLoadedData={handleLoadedData}
          onError={handleError}
        />
      )}
    </div>
  );
}
